// =============================================================================
// Ridimensionamento immagini lato client
// =============================================================================
// Il logo dell'organizzazione viaggia verso l'API come data URL base64: viene
// ridotto nel browser prima dell'invio, così l'utente può caricare la foto che
// ha senza superare i limiti dell'endpoint (512KB decodificati).
// =============================================================================

/// Lato massimo del logo salvato
const DEFAULT_MAX_SIDE = 512;
/// Tetto sui byte decodificati, allineato al backend (MAX_LOGO_BYTES)
const MAX_ENCODED_BYTES = 512 * 1024;
/// Limite sul file sorgente: oltre, l'immagine non viene nemmeno decodificata
export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

export class ImageTooLargeError extends Error {}
export class ImageDecodeError extends Error {}

/// Byte reali codificati in un data URL base64
export function dataUrlByteLength(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/**
 * Ridimensiona un'immagine mantenendo le proporzioni e la restituisce come
 * data URL. Prova prima WebP (compatto e con trasparenza), poi PNG; se il
 * risultato resta troppo pesante ripiega su JPEG su fondo bianco.
 */
export async function resizeImageToDataUrl(
  file: File,
  maxSide = DEFAULT_MAX_SIDE,
): Promise<string> {
  if (file.size > MAX_SOURCE_BYTES) {
    throw new ImageTooLargeError('File troppo grande');
  }

  const bitmap = await loadImage(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageDecodeError('Canvas non disponibile');
  ctx.drawImage(bitmap, 0, 0, width, height);

  // 1) WebP: il formato più compatto che conserva la trasparenza. I browser che
  //    non lo supportano restituiscono silenziosamente un PNG, comunque valido.
  const preferred = canvas.toDataURL('image/webp', 0.92);
  if (dataUrlByteLength(preferred) <= MAX_ENCODED_BYTES) {
    return preferred;
  }

  // 2) JPEG su fondo bianco: perde la trasparenza ma comprime le foto pesanti
  ctx.globalCompositeOperation = 'destination-over';
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  for (const quality of [0.85, 0.7, 0.55]) {
    const jpeg = canvas.toDataURL('image/jpeg', quality);
    if (dataUrlByteLength(jpeg) <= MAX_ENCODED_BYTES) {
      return jpeg;
    }
  }

  throw new ImageTooLargeError('Immagine troppo pesante anche dopo la compressione');
}

/// Decodifica il file usando createImageBitmap quando disponibile
async function loadImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Formati non supportati da createImageBitmap: si ripiega su <img>
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new ImageDecodeError('Immagine non leggibile'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
